/**
 * React side of builder codes (#770): the configured builder for the selected network, the connected user's approval
 * (/info maxBuilderFee, cached per env + user + builder) and the one-time approveBuilderFee flow. With builder codes
 * off (the default, see builder.ts) this hook does nothing: no /info call, no approval, no fee.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import type { Hex } from 'viem';
import { useEnv } from '../config/env';
import { useWallet } from '../wallet/context';
import { isUserRejection, walletErrorMessage } from '../wallet/errors';
import {
  approvalFor,
  approveBuilderFee,
  builderConfigFor,
  BuilderApproveError,
  builderField,
  fetchMaxBuilderFee,
  type BuilderApproval,
  type BuilderConfig,
} from './builder';
import type { BuilderInfo } from '../hl/signing';
import { t } from '../i18n/i18n';
import { browseOnlyMessage, useGeo } from '../geo/context';

export interface UseBuilder {
  /** Null = builder codes off: send no builder field, show no fee. */
  cfg: BuilderConfig | null;
  /** The order action's `builder` field (null when off). */
  field: BuilderInfo | null;
  approval: BuilderApproval;
  /** Approved maximum in tenths of a bp, once known. */
  maxApproved: number | null;
  approving: boolean;
  error: string | null;
  /** Wallet signs approveBuilderFee for the configured fee. Resolves true when Hyperliquid reports it approved. */
  approve: () => Promise<boolean>;
  /** Re-read maxBuilderFee (e.g. after Hyperliquid rejected an order for a missing approval). */
  recheck: () => void;
}

export function useBuilder(user: string | null, opts: { fetchImpl?: typeof fetch } = {}): UseBuilder {
  const { env } = useEnv();
  const wallet = useWallet();
  const qc = useQueryClient();
  const cfg = useMemo(() => builderConfigFor(env.name), [env.name]);
  const me = user?.toLowerCase() ?? null;
  const key = useMemo(() => ['builder', 'maxBuilderFee', env.name, me, cfg?.address ?? null] as const, [env.name, me, cfg]);
  const fetchImpl = opts.fetchImpl;
  const q = useQuery<number, Error>({
    queryKey: key,
    queryFn: () => fetchMaxBuilderFee(env, me as string, (cfg as BuilderConfig).address, fetchImpl),
    enabled: !!cfg && !!me,
    staleTime: 60_000,
    retry: 1,
    refetchOnWindowFocus: true,
  });
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // #769: no approvals while browse-only applies (the ticket hides the approval panel; this is defence in depth).
  const geo = useGeo();
  const geoCanTrade = geo.canTrade;
  const geoDecision = geo.decision;

  const approve = useCallback(async (): Promise<boolean> => {
    if (!geoCanTrade) {
      setError(browseOnlyMessage(geoDecision));
      return false;
    }
    if (!cfg || !me || !wallet.provider || wallet.status !== 'connected' || !wallet.address) return false;
    setApproving(true);
    setError(null);
    try {
      const n = await approveBuilderFee({ provider: wallet.provider, account: wallet.address as Hex, env, cfg, fetchImpl });
      qc.setQueryData(key, n);
      if (n < cfg.fee) {
        setError(t('builder.not_reported'));
        return false;
      }
      return true;
    } catch (e) {
      if (isUserRejection(e)) setError(t('builder.canceled'));
      else if (e instanceof BuilderApproveError) setError(t('common.approval_refused', { error: e.message }));
      else setError(t('wallet.error_generic', { error: walletErrorMessage(e) }));
      return false;
    } finally {
      setApproving(false);
    }
  }, [geoCanTrade, geoDecision, cfg, me, wallet.provider, wallet.status, wallet.address, env, fetchImpl, qc, key]);

  const recheck = useCallback(() => {
    void qc.invalidateQueries({ queryKey: key, exact: true });
  }, [qc, key]);

  return {
    cfg,
    field: builderField(cfg),
    approval: approvalFor(cfg, q.data ?? null, q.isError && q.data === undefined),
    maxApproved: q.data ?? null,
    approving,
    error,
    approve,
    recheck,
  };
}
